import React, { ReactNode, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import ErrorBoundary from './components/ErrorBoundary';
import ThemeProvider from './components/ThemeProvider';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import PausedAccountBanner from './components/PausedAccountBanner';
import DevSiteBanner from './components/DevSiteBanner';
import './App.css';

// Route-level code splitting: each page loads only when first visited, so the
// initial bundle doesn't carry Recharts, the forum, the admin panel, etc.
const Home            = lazy(() => import('./pages/Home'));
const CommunityFeed   = lazy(() => import('./pages/CommunityFeed'));
const Profile         = lazy(() => import('./pages/Profile'));
const TalkShop        = lazy(() => import('./pages/TalkShop'));
const TalkShopThread  = lazy(() => import('./pages/TalkShopThread'));
const Login           = lazy(() => import('./pages/Login'));
const Signup          = lazy(() => import('./pages/Signup'));
const Analyze         = lazy(() => import('./pages/Analyze'));
const PrivacyTerms    = lazy(() => import('./pages/PrivacyTerms'));
const Admin           = lazy(() => import('./pages/Admin'));
const AccountSettings = lazy(() => import('./pages/AccountSettings'));

// ---------------------------------------------------------------------------
// ProtectedRoute — requires authentication, redirects to login otherwise
// ---------------------------------------------------------------------------

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <>{children}</> : <Navigate to="/login" replace />;
}

// ---------------------------------------------------------------------------
// AdminRoute — renders children only for admin users, redirects everyone else
// ---------------------------------------------------------------------------

function AdminRoute({ children }: { children: ReactNode }) {
  const { isAdmin, loading, profileLoading } = useAuth();
  // Wait for both auth AND the profile row before deciding — if we redirect
  // while profileLoading is true we'll incorrectly redirect admins whose
  // profile hasn't arrived yet.
  if (loading || profileLoading) return null;
  return isAdmin ? <>{children}</> : <Navigate to="/" replace />;
}

// Legacy /account → /settings, carrying the query string across so Stripe
// redirect banners (?checkout=success|cancelled) survive the rename.
function AccountRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/settings${search}`} replace />;
}

// Footer is hidden on the full-viewport Pull Library page
function FooterWrapper() {
  const { pathname } = useLocation();
  if (pathname === '/pull-library') return null;
  return <Footer />;
}

// ---------------------------------------------------------------------------
// Main app content
// ---------------------------------------------------------------------------

function AppContent() {
  const { user, signOut, loading, isPaused, isAdmin } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <Router>
      <div className="App">
        {/* Steer users off staging / preview deploys onto production */}
        <DevSiteBanner />

        <Navbar
          isLoggedIn={!!user}
          isAdmin={isAdmin}
          onLogout={async () => { await signOut(); }}
        />

        {/* Shown at the top of every page when the account is paused */}
        {isPaused && <PausedAccountBanner />}

        <main className="main-content">
          <Suspense fallback={<div className="loading">Loading...</div>}>
          <Routes>
            <Route path="/"             element={<Home />} />
            <Route path="/feed"         element={<ProtectedRoute><CommunityFeed /></ProtectedRoute>} />
            <Route path="/profile"      element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/talkshop"           element={<ProtectedRoute><TalkShop /></ProtectedRoute>} />
            <Route path="/talkshop/:threadId" element={<ProtectedRoute><TalkShopThread /></ProtectedRoute>} />
            <Route path="/login"        element={<Login onLoginSuccess={() => {}} />} />
            <Route path="/signup"       element={<Signup />} />
            <Route path="/pull-library" element={<ProtectedRoute><Analyze /></ProtectedRoute>} />
            <Route path="/privacy-terms" element={<PrivacyTerms />} />
            <Route path="/settings"     element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
            {/* Legacy /account → /settings, preserving any query (e.g. Stripe ?checkout=success) */}
            <Route path="/account"      element={<AccountRedirect />} />

            {/* Admin panel — redirects non-admins to home */}
            <Route
              path="/admin/*"
              element={
                <AdminRoute>
                  <Admin />
                </AdminRoute>
              }
            />
          </Routes>
          </Suspense>
        </main>
        <FooterWrapper />
      </div>
    </Router>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ThemeProvider>
          <AppContent />
        </ThemeProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
